/*
Componente modal para configuracion de cookies
CORREGIDO para Safari (Node 16.20.2, React 16.12.0):

Mejoras clave para compatibilidad con Safari/macOS/iOS:
- React Portal (createPortal) -> renderiza en document.body, fuera del
  arbol React, evitando problemas de stacking context y transforms de padres
- Siempre montado -> los event listeners se mantienen attached, evitando
  el bug de React 16 + Safari con re-attachment de listeners
- useCallback -> referencias estables de handlers para event delegation
- visibility/opacity en lugar de condicional return -> handlers siempre activos
- display controlado por clase CSS 'cookie-modal-open' -> compatible Safari
- Sin transform: translateY -> evita crear stacking context nuevo
- Sin backdrop-filter -> Safari no intercepta click events
- Con Esc key handler -> cierre alternativo en Safari/iOS

Mantiene API publica:
- props: open, onClose, onAcceptAll, onAcceptSelected (sin cambios)
- data-* attrs: data-lang (sin cambios para i18n)
- clases CSS: cookie-modal-overlay, cookie-modal-container, etc. (sin cambios)
*/

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './CookieConsent.css'

export default function CookieModal({ open, onClose, onAcceptAll, onAcceptSelected }) {
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true,
    analytics: false,
    functional: false
  })

  // Handler estable con useCallback (Safari-friendly, evita event listener detach)
  const handleCookieChange = useCallback((type) => (event) => {
    // Safari a veces no propaga bien stopPropagation en synthetic events
    if (event && event.stopPropagation) {
      event.stopPropagation()
    }
    const checked = event && event.target ? event.target.checked : false
    setCookieSettings(prev => ({
      ...prev,
      [type]: checked
    }))
  }, [])

  const handleAcceptAll = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation()
    const allAccepted = {
      necessary: true,
      analytics: true,
      functional: true
    }

    if (typeof console !== 'undefined' && console.log) {
      console.log('COOKIE CONSENT - Aceptar Todo:', {
        timestamp: new Date().toISOString(),
        action: 'accept_all',
        preferences: allAccepted
      })
    }

    setCookieSettings(allAccepted)
    if (onAcceptAll) onAcceptAll(allAccepted)
    if (onClose) onClose()
  }, [onAcceptAll, onClose])

  const handleSaveSelection = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation()
    const selectedPreferences = {
      necessary: true,
      ...(cookieSettings.analytics && { analytics: true }),
      ...(cookieSettings.functional && { functional: true })
    }

    if (typeof console !== 'undefined' && console.log) {
      console.log('COOKIE CONSENT - Guardar Seleccion:', {
        timestamp: new Date().toISOString(),
        userSelection: cookieSettings,
        savedPreferences: selectedPreferences,
        acceptedCategories: Object.keys(selectedPreferences).filter(key => selectedPreferences[key]),
        rejectedCategories: Object.keys(cookieSettings).filter(key => !cookieSettings[key] && key !== 'necessary')
      })
    }

    if (onAcceptSelected) onAcceptSelected(selectedPreferences)
    if (onClose) onClose()
  }, [cookieSettings, onAcceptSelected, onClose])

  const handleRejectAll = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation()
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      functional: false
    }

    if (typeof console !== 'undefined' && console.log) {
      console.log('COOKIE CONSENT - Solo Esenciales:', {
        timestamp: new Date().toISOString(),
        action: 'reject_all_optional',
        preferences: onlyNecessary
      })
    }

    setCookieSettings(onlyNecessary)
    if (onAcceptSelected) onAcceptSelected(onlyNecessary)
    if (onClose) onClose()
  }, [onAcceptSelected, onClose])

  const handleOverlayClick = useCallback((e) => {
    // Solo cierra si se hace click directamente sobre el overlay,
    // no sobre el container hijo (que hace stopPropagation)
    if (e && e.stopPropagation) e.stopPropagation()
    if (e.target === e.currentTarget && onClose) {
      onClose()
    }
  }, [onClose])

  const handleCloseClick = useCallback((e) => {
    if (e && e.stopPropagation) e.stopPropagation()
    if (onClose) onClose()
  }, [onClose])

  // Keyboard: Esc para cerrar (Safari-friendly)
  useEffect(() => {
    if (!open) return undefined
    const handleKeydown = (event) => {
      if (event.key === 'Escape' || event.keyCode === 27) {
        if (onClose) onClose()
      }
    }
    document.addEventListener('keydown', handleKeydown)
    return () => {
      document.removeEventListener('keydown', handleKeydown)
    }
  }, [open, onClose])

  // Bloquear scroll del body cuando modal esta abierto (Safari iOS + rubber-band)
  // En iOS Safari es necesario position:fixed ademas de overflow:hidden para
  // prevenir el rubber-band scroll. Tambien preservamos la posicion de scroll
  // para que el usuario vuelva al mismo punto al cerrar.
  const scrollLockStateRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return undefined
    }
    const body = document.body
    const htmlElement = document.documentElement

    scrollLockStateRef.current = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      scrollY: window.scrollY || window.pageYOffset || 0
    }

    const scrollY = scrollLockStateRef.current.scrollY
    body.style.overflow = 'hidden'
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.width = '100%'
    htmlElement.style.overflow = 'hidden'

    return () => {
      const saved = scrollLockStateRef.current
      if (!saved) return
      body.style.overflow = saved.overflow
      body.style.position = saved.position
      body.style.top = saved.top
      body.style.width = saved.width
      htmlElement.style.overflow = ''
      // Restaurar scroll del usuario
      window.scrollTo(0, saved.scrollY)
      scrollLockStateRef.current = null
    }
  }, [open])

  // Guard SSR: en build de React 16 con SSR, document puede no existir
  if (typeof document === 'undefined') {
    return null
  }

  // Renderizar via Portal fuera del arbol React al final del body
  // Asi evitamos problemas con transform/position de elementos padres en Safari
  const modalContent = (
    <div
      className={'cookie-modal-overlay' + (open ? ' cookie-modal-open' : '')}
      onClick={handleOverlayClick}
      role='dialog'
      aria-modal='true'
      aria-hidden={!open}
      data-cookie-modal='true'
    >
      <div
        className='cookie-modal-container'
        onClick={(e) => { if (e && e.stopPropagation) e.stopPropagation() }}
      >
        <div className='cookie-modal-header'>
          <h2 className='cookie-modal-title' data-lang="cookie_title">
            Configuracion de Cookies
          </h2>
          <button
            type='button'
            className='cookie-modal-close-btn'
            onClick={handleCloseClick}
            aria-label="Cerrar modal de cookies"
          >
            ✕
          </button>
        </div>

        <div className='cookie-modal-body'>
          <p className='cookie-modal-desc' data-lang="cookie_description">
            Utilizamos cookies para mejorar tu experiencia en nuestro sitio web.
            Puedes configurar que tipos de cookies deseas aceptar.
          </p>

          <div className='cookie-modal-section'>
            <div className='cookie-modal-section-header'>
              <h3 className='cookie-modal-section-title' data-lang="cookie_necessary_title">
                Cookies Necesarias
              </h3>
              <label className='cookie-switch'>
                <input
                  type='checkbox'
                  checked={cookieSettings.necessary}
                  disabled
                  readOnly
                />
                <span className='cookie-slider disabled'></span>
              </label>
            </div>
            <p className='cookie-modal-section-desc' data-lang="cookie_necessary_description">
              Estas cookies son esenciales para el funcionamiento del sitio web.
            </p>
            <span className='cookie-modal-status' data-lang="cookie_always_active">Siempre activadas</span>
          </div>

          <hr className='cookie-modal-divider' />

          <div className='cookie-modal-section'>
            <div className='cookie-modal-section-header'>
              <h3 className='cookie-modal-section-title' data-lang="cookie_analytics_title">
                Cookies de Analisis
              </h3>
              <label className='cookie-switch'>
                <input
                  type='checkbox'
                  checked={cookieSettings.analytics}
                  onChange={handleCookieChange('analytics')}
                />
                <span className='cookie-slider'></span>
              </label>
            </div>
            <p className='cookie-modal-section-desc' data-lang="cookie_analytics_description">
              Nos ayudan a entender como interactuas con el sitio web.
            </p>
            <span className='cookie-modal-status' data-lang="cookie_analytics_allow">Permitir cookies de analisis</span>
          </div>

          <hr className='cookie-modal-divider' />

          <div className='cookie-modal-section'>
            <div className='cookie-modal-section-header'>
              <h3 className='cookie-modal-section-title' data-lang="cookie_functional_title">
                Cookies Funcionales
              </h3>
              <label className='cookie-switch'>
                <input
                  type='checkbox'
                  checked={cookieSettings.functional}
                  onChange={handleCookieChange('functional')}
                />
                <span className='cookie-slider'></span>
              </label>
            </div>
            <p className='cookie-modal-section-desc' data-lang="cookie_functional_description">
              Permiten funcionalidades mejoradas y personalizacion.
            </p>
            <span className='cookie-modal-status' data-lang="cookie_functional_allow">Permitir cookies funcionales</span>
          </div>
        </div>

        <div className='cookie-modal-actions'>
          <button
            type='button'
            className='cookie-btn cookie-btn-secondary'
            onClick={handleRejectAll}
            data-lang="cookie_essential_only"
          >
            Solo Esenciales
          </button>
          <button
            type='button'
            className='cookie-btn cookie-btn-primary-outline'
            onClick={handleSaveSelection}
            data-lang="cookie_save"
          >
            Guardar Seleccion
          </button>
          <button
            type='button'
            className='cookie-btn cookie-btn-primary'
            onClick={handleAcceptAll}
            data-lang="cookie_accept_all"
          >
            Aceptar Todo
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}
